import { getDeepObjectValuesByKey } from '@prismamedia/one-components';
import { cloneDeep, isUndefined } from 'lodash';
import type { ExtractRouteParams } from 'react-router';
import { generatePath, matchPath } from 'react-router-dom';
import { history } from '../../history';
import { paths } from '../../routing/Router/paths';
import { validateSlug } from '../../utils/slugValidation';
import {
  ALLOWED_CATEGORY_STATUES,
  CATEGORY_TITLE_REQUIRED_ERROR_MESSAGE,
  DEFAULT_ROOT_CATEGORY,
} from './constants';
import type { CategoryList, CategoryErrors, CategoryForm } from './types';
import { ErrorMessages } from './types/index';

/// CATEGORY UTILS ////////////////////////////////////////////
export const getBrandCategoriesWithPath = <T extends unknown>(
  categories: T,
  prevPathSegments = '',
): CategoryList[] => {
  if (!Array.isArray(categories)) {
    return [];
  }

  return categories.map((category) => {
    const isRootCategory = category.level <= DEFAULT_ROOT_CATEGORY.level;
    const path = isRootCategory
      ? `/${category.id}`
      : `${prevPathSegments}/${category.id}`;

    // Process children if they exist
    const children = category.children
      ? getBrandCategoriesWithPath(category.children, path)
      : [];

    return {
      ...category,
      path,
      children,
    };
  });
};

export const getCategoryStatusLabel = (targetStatus?: string): string =>
  ALLOWED_CATEGORY_STATUES?.filter((status) => status.id === targetStatus)[0]
    ?.label || '';

const getCurrentPath = () =>
  matchPath(history.location.pathname, Object.values(paths))?.path || '';

export const getActivePathCategoryId = (
  routeParams: Record<string, any>,
): string =>
  routeParams.lvl5 ||
  routeParams.lvl4 ||
  routeParams.lvl3 ||
  routeParams.lvl2 ||
  routeParams.lvl1 ||
  '';

/// ROUTE UTILS ////////////////////////////////////////////
export const isPageCreateView = () =>
  !!matchPath(history.location.pathname, { path: paths.PAGE_CREATE });

export const isPageEditView = () =>
  !!matchPath(history.location.pathname, { path: paths.PAGE_EDIT });

interface NavigateToProps {
  pathname?: string;
  opts?: {
    method?: 'PUSH' | 'POP' | 'REPLACE';
    hash?: string;
    search?: string;
  };
  routeParams?: ExtractRouteParams<any>;
}

export const navigateTo = (params: NavigateToProps) => {
  const { pathname, routeParams = {}, opts } = params;

  // By default the used pathname is the current one
  let finalPathname = history.location.pathname;

  // If a pathname is passed as param, we using it
  if (pathname) {
    const isPathIsRoutePattern = pathname?.includes(':');
    finalPathname = isPathIsRoutePattern
      ? generatePath(pathname, routeParams)
      : pathname;
  }

  // If routeParams is passed as param, we using it too
  else if (routeParams.brandKey && routeParams.format) {
    finalPathname = generatePath(getCurrentPath(), routeParams);
  }

  // We keeping current hash and search params if they're exists or using those are passed as "opts" param
  (history as any)[(opts?.method || 'PUSH').toLowerCase()]({
    hash: !isUndefined(opts?.hash) ? opts?.hash : history.location.hash,
    pathname: finalPathname,
    search: !isUndefined(opts?.search) ? opts?.search : history.location.search,
  });
};

/// ERROR FORM CATEGORY UTILS ////////////////////////////////////////////
export const getIsFormValid = (errors: CategoryErrors) =>
  !getDeepObjectValuesByKey(errors, 'errors').length;

interface GetComputedCategoryFormErrorsProps {
  errors: CategoryErrors;
  form: CategoryForm;
  path?: string[];
}
export const getCategoryFormErrors = ({
  errors,
  form,
  path,
}: GetComputedCategoryFormErrorsProps): CategoryErrors => {
  const isInitialization = !path;
  let newErrors = cloneDeep(errors);

  // Title check
  if (isInitialization || path[0] === 'title') {
    const hasRequiredRuleError = !form.title?.length;
    const hasError = hasRequiredRuleError;
    const errorList = [
      ...((hasRequiredRuleError && [ErrorMessages.requiredRule]) || []),
    ];

    newErrors = {
      ...newErrors,
      title: {
        errorMessages: {
          [ErrorMessages.requiredRule]: CATEGORY_TITLE_REQUIRED_ERROR_MESSAGE,
        },
        errors: errorList,
        hasError,
        isTouched: !isInitialization,
      },
    };
  }

  // Slug validation
  if (isInitialization || path[0] === 'slug') {
    if (form.slug) {
      const slugError = validateSlug(form.slug);
      const hasSlugError = !!slugError;
      const errorList = [
        ...((hasSlugError && [ErrorMessages.patternRule]) || []),
      ];

      newErrors = {
        ...newErrors,
        slug: {
          errorMessages: {
            [ErrorMessages.patternRule]: slugError,
          },
          errors: errorList,
          hasError: hasSlugError,
          isTouched: !isInitialization,
        },
      };
    }
  }

  return newErrors;
};

/// VALIDATION UTILS ////////////////////////////////////////////
interface ValidateCategoryType {
  isValid: boolean;
  errorMessage: string;
}

export const validateCategory = (form: CategoryForm): ValidateCategoryType => {
  // Title validation
  if (!form.title || form.title.trim() === '') {
    return {
      isValid: false,
      errorMessage: 'Le titre est requis',
    };
  }

  // Slug validation
  if (form.slug) {
    const slugError = validateSlug(form.slug);
    if (slugError) {
      return {
        isValid: false,
        errorMessage: slugError,
      };
    }
  }

  // Sections title validation
  if (form.sections && form.sections.length > 0) {
    const missingTitleIndex = form.sections.findIndex(
      (section) => !section.title || section.title.trim() === '',
    );

    if (missingTitleIndex !== -1) {
      return {
        isValid: false,
        errorMessage: `Titre de la section ${missingTitleIndex + 1} est requis`,
      };
    }
  }

  return {
    isValid: true,
    errorMessage: '',
  };
};

/// ERROR UTILS ////////////////////////////////////////////
export const getErrorMessages = (
  errors: any = {},
  messages: string[] = [],
): string[] =>
  Object.keys(errors).reduce((errorMessages: string[], key: string) => {
    const currentSectionErrors = errors[key];

    if (!currentSectionErrors || !('errors' in currentSectionErrors)) {
      return getErrorMessages(currentSectionErrors, errorMessages);
    }

    if (currentSectionErrors.hasError) {
      const currentSectionErrorsMessages = currentSectionErrors.errors.map(
        (errorType: string) =>
          currentSectionErrors.errorMessages[errorType] || '',
      );

      // eslint-disable-next-line fp/no-mutating-methods
      errorMessages.push(...currentSectionErrorsMessages);
    }

    return errorMessages;
  }, messages);
